import React, { useEffect, useState, useContext, useCallback } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { modeLabels } from "../../utils/constants/deviceConstants";
import Pagination from "../../utils/components/common/pagination";
import { timeStringToTimeAMPM } from "../../utils/utils";
import {
  UserContext,
  OrganisationContext,
  ReportsDownloadUpdate,
} from "../../layouts/MainLayout";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import Icons from "../../utils/components/icons";
import { getReportsForAUser } from "../../services/ReportService";
import { getAllDeviceList } from "../../services/DeviceService";

import "./ReportDownloads.scss";
import { getUsersForTenant } from "../../services/UserService";
import { getLocationAndHierarchyDetails } from "../../services/LocationService";

const ReportDownloads = () => {
  const [downloadLists, setDownloadLists] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [pageTotalCount, setPageTotalCount] = useState(0);
  const [newReportDownloads, setNewReportDownloads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadedStatus, setDownloadedStatus] = useState({});
  const [deviceList, setDeviceList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [locationDetailsList, setLocationDetailsList] = useState([]);

  const queryCount = 11;
  const { t } = useTranslation("reports");
  const { siteData } = useContext(OrganisationContext);
  const { userCognitoData } = useContext(UserContext);
  const { reportUpdateStatus, setReportUpdateStatus } = useContext(
    ReportsDownloadUpdate
  );

  const { userData } = useContext(UserContext);

  const columnList = [
    t("downloadsTableColumn.requestedTimestamp"),
    t("downloadsTableColumn.downloadLink"),
    t("downloadsTableColumn.status"),
    t("downloadsTableColumn.reportType"),
    t("downloadsTableColumn.filterApplied"),
  ];

  const getReportJobId = useCallback(() => {
    const list = [];
    [...reportUpdateStatus].forEach((elem) => {
      list.push(elem.reportJobId);
    });
    setReportUpdateStatus((prev) => {
      return [];
    });
    setNewReportDownloads(list);
  }, [reportUpdateStatus, setReportUpdateStatus]);

  const getReportsForUser = useCallback(() => {
    setIsLoading(true);
    const param = {
      userId: userCognitoData.username,
      filters: {
        siteId: [siteData.id],
      },
      sort: [
        {
          requestedDateTime: "DESC",
        },
      ],
      scrollID: null,
    };
    getReportsForAUser(param)
      .then((resp) => {
        setIsLoading(false);
        if (resp?.data?.Items) {
          setDownloadLists(resp?.data?.Items);
          setPageTotalCount(Math.ceil(resp.data.TotalReportCount / queryCount));
        }
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, [siteData.id, userCognitoData.username]);

  const getDeviceList = useCallback(async () => {
    try {
      const tenantId = siteData.id;

      const queryParams = {
        tenantId: tenantId,
        sort: [{ DeviceName: "asc" }],
        projectionFields: ["DeviceName", "deviceUUIDFromTelemetry", "PK"],
      };
      const response = await getAllDeviceList(queryParams);

      if (response?.data?.Items) {
        setDeviceList(response?.data?.Items);
      }
    } catch (error) {
      // console.log("error => ", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserList = useCallback(async () => {
    try {
      const queryParams = {
        tenantId: userData?.OrganizationID,
        requestedByUserId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
        sort: [{ email: "asc" }],
      };

      const response = await getUsersForTenant(queryParams);

      if (response?.data?.Items) {
        setUserList(response?.data?.Items);
      }
    } catch (error) {}
  }, [userCognitoData, userData]);

  const getLocationHierarchyDetails = useCallback(() => {
    const queryParams = {
      organizationId: userData?.OrganizationID,
      locationHierarchy: userData?.OrganizationID + "#" + siteData.id,
      requestedByUserId: userCognitoData.username,
      getLocationDeletableStatus: true,
    };
    getLocationAndHierarchyDetails(queryParams)
      .then((resp) => {
        if (resp) {
          setLocationDetailsList(resp?.data?.Items?.Level1);
        }
      })
      .catch((error) => {});
  }, [siteData.id, userCognitoData.username, userData?.OrganizationID]);

  function findLocationHierarchy(dataArray, targetPK) {
    function searchInLevels(data, targetPK, path) {
      path.push({ PK: data.PK, LocationName: data.LocationName });

      if (data.PK === targetPK) {
        return path;
      }

      if (data.Level2) {
        for (const level2 of data.Level2) {
          const result = searchInLevels(level2, targetPK, [...path]);
          if (result) return result;
        }
      }

      if (data.Level3) {
        for (const level3 of data.Level3) {
          const result = searchInLevels(level3, targetPK, [...path]);
          if (result) return result;
        }
      }
      path.pop();
      return null;
    }

    for (const data of dataArray) {
      const result = searchInLevels(data, targetPK, []);
      if (result) return result;
    }

    return null;
  }

  useEffect(() => {
    getLocationHierarchyDetails();
  }, [getLocationHierarchyDetails]);

  useEffect(() => {
    getDeviceList();
  }, [getDeviceList]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const systemStatusFormatter = useCallback((data) => {
      if(data === "UpdateAvailable"){
        return t("reportFilter.chorusUpdateDeviceList.updateAvailable");
      } else if(data === "UpToDate"){
        return t("reportFilter.chorusUpdateDeviceList.systemUpToDate");
      } else if(data === "InProgress"){
        return t("reportFilter.chorusUpdateDeviceList.updateInProgress");
      }
  }, [t]);

  const clo2Formatter = useCallback((data) => {
    if(data === "Below70PctOfTarget"){
      return t("reportFilter.deviceDiagnostics.below70");
    } else if(data === "Beyond130PctOfTarget"){
      return t("reportFilter.deviceDiagnostics.beyond130");
    } else if(data === "Beyond150PctOfTarget"){
      return t("reportFilter.deviceDiagnostics.beyond150");
    } else if(data === "Within30PctOfTarget"){
      return t("reportFilter.deviceDiagnostics.within30");
    }
}, [t]);

  const cartridgeFormatter = useCallback((data) => {
    return data
      .map((item) => {
        if(item[0] === 0){
          return t("reportFilter.deviceDiagnostics.below10");
        } else if(item[0] === 10){
          return t("reportFilter.deviceDiagnostics.10_19");
        } else if(item[0] === 20){
          return t("reportFilter.deviceDiagnostics.20AndAbove");
        }
        return undefined;
      })
      .filter(Boolean);
  }, [t]);

  
  const renderFilters = (item) => {
    if (
      item.ReportType === "Device Diagnostics" ||
      item.ReportType === "Device Parameter List" ||
      item.ReportType === "Device Summary"
    ) {
      const filterList = [];
      if (item.Filters?.deviceId) {
        item.Filters?.deviceId.forEach((elem) => {
          if (deviceList.length) {
            deviceList.forEach((elemDeviceList) => {
              if (elemDeviceList.deviceUUIDFromTelemetry === elem) {
                let device = <span>{elemDeviceList.DeviceName}</span>;
                filterList.push(device);
              }
            });
          }
        });
      }

      if (deviceList.length === filterList.length) {
        while (filterList.length > 0) {
          filterList.pop();
        }
        filterList.push(<span>All Devices</span>);
      }

      if (item.Filters?.timeStamp && item.Filters?.timeStamp?.between) {
        item.Filters?.timeStamp?.between.forEach((elem, index) => {
          let mode = (
            <span>{`${index > 0 ? "To" : "From"}: ${timeStringToTimeAMPM(
              elem
            )}`}</span>
          );
          filterList.push(mode);
        });
      }

      if (item.Filters?.mode) {
        let modeList = [];
        item.Filters?.mode.forEach((elem) => {
          modeList.push(modeLabels[elem]);
        });
        let mode = <span>{`Mode: ${modeList.join(" / ")}`}</span>;
        filterList.push(mode);
      }
      if (item.Filters?.humidity && item.Filters?.humidity?.between) {
        let mode = (
          <span>{`Humidity: ${item.Filters?.humidity?.between.join(
            " - "
          )} %`}</span>
        );
        filterList.push(mode);
      }
      if (item.Filters?.clo2Value) {
          let data = item.Filters?.clo2Value;
          const validKeys = Object.keys(data).filter(key => data[key] === true);
          if (validKeys.length) {
            let ppbList = [];
            validKeys.forEach((elem) => {
              ppbList.push(clo2Formatter(elem));
            });
            let ppbStatus = <span>{`PPB: ${ppbList.join(" / ")}`}</span>;
            filterList.push(ppbStatus);
          }
      }
      if (item.Filters?.cartridgeLevel) {
        if (item.Filters?.cartridgeLevel?.between.length) {
          let val = cartridgeFormatter(item.Filters?.cartridgeLevel?.between);
          let crtg = <span>{`Cartridge: ${val.join(" / ")}`}</span>;
          filterList.push(crtg);
        }
      }
      return filterList;
    }

    if (item.ReportType === "AuditTrail") {
      const filterList = [];

      if (item.Filters?.timeStamp && item.Filters?.timeStamp?.between) {
        item.Filters?.timeStamp?.between.forEach((elem, index) => {
          let time = (
            <span>{`${index > 0 ? "To" : "From"}: ${timeStringToTimeAMPM(
              elem
            )}`}</span>
          );
          filterList.push(time);
        });
      }

      if (item.Filters?.userActionTypes) {
        let userActionList = [];
        item.Filters?.userActionTypes.forEach((elem) => {
          userActionList.push(
            t(`reportFilter.auditTrail.userActionTypeEnums.${elem}`)
          );
        });
        let userActions = (
          <span>{`User Action Types: ${userActionList.join(" / ")}`}</span>
        );
        filterList.push(userActions);
      }

      if (item.Filters?.devices) {
        item.Filters?.devices.forEach((elem) => {
          if (deviceList.length) {
            deviceList.forEach((elemDeviceList) => {
              if (elemDeviceList.deviceUUIDFromTelemetry === elem) {
                let device = <span>{elemDeviceList.DeviceName}</span>;
                filterList.push(device);
              }
            });
          }
        });
      }

      if (deviceList.length === filterList.length) {
        while (filterList.length > 0) {
          filterList.pop();
        }
        filterList.push(<span>All Devices</span>);
      }

      if (item.Filters?.users) {
        item.Filters?.users.forEach((elem) => {
          if (userList.length) {
            userList.forEach((elemUserList) => {
              if (elemUserList.PK === `U#${elem}`) {
                let user = <span>{elemUserList.Email}</span>;
                filterList.push(user);
              }
            });
          }
        });
      }

      return filterList;
    }

    if (item.ReportType === "DeviceUpdateList") {
      const filterList = [];

      if (item.Filters?.deviceLocationHierarchy) {
        let locationPK = item.Filters?.deviceLocationHierarchy;
        let result = findLocationHierarchy(locationDetailsList, locationPK);
        let locationLists = [];

        if(result){
          result.forEach((item) => {
            locationLists.push(item.LocationName)
          })
        }
        filterList.push(<span>{`Location: ${locationLists.join(', ')}`}</span>);
      }

      if (item.Filters?.model?.length) {
        let modelList = [];
        item.Filters?.model.forEach((elem) => {
          modelList.push(elem);
        });
        let modelLists = <span>{`Model: ${modelList.join(" / ")}`}</span>;
        filterList.push(modelLists);
      }

      if (item.Filters?.otaStatus?.length) {
        let otaStatusList = [];
        item.Filters?.otaStatus.forEach((elem) => {
          otaStatusList.push(systemStatusFormatter(elem));
        });
        let otaStatus = <span>{`System Status: ${otaStatusList.join(" / ")}`}</span>;
        filterList.push(otaStatus);
      }

      if (item.Filters?.version?.length) {
        let versionList = [];
        item.Filters?.version.forEach((elem) => {
          versionList.push(elem);
        });
        let versionLists = <span>{`Version: ${versionList.join(" / ")}`}</span>;
        filterList.push(versionLists);
      }

      return filterList;
    }

    return null;
  };

  const downloadFile = (fileLink) => {
    let link = document.createElement("a");
    link.href = fileLink;
    link.download = true;
    link.target = "_blank";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // window.open(fileLink, "_blank");
  };

  useEffect(() => {
    if (userCognitoData?.username && siteData?.id) {
      getReportsForUser();
    }
  }, [
    getReportsForUser,
    pageCount,
    siteData,
    userCognitoData,
    reportUpdateStatus,
  ]);

  useEffect(() => {
    if (reportUpdateStatus.length > 0) {
      getReportJobId();
    }
  }, [getReportJobId, reportUpdateStatus]);

  const generateReportType = (type) =>{

    if(type === "AuditTrail"){
      return t("auditTrailHeading");
    } else if (type === "DeviceUpdateList"){
      return t("updateDeviceListHeading");
    }

    return type;
  } 

  return (
    <div className="bodyInner">
      <div className="bodyContainer">
        <div className="controllesWrapper">
          <div className="d-flex align-items-center breadCrumb">
            <Link to="/reports">{t("reportsHeading")}</Link>
            <span>/</span>
            <span id="downloads_title">{t("downloadsTitle")}</span>
          </div>
        </div>
        <div className="tableWrapper">
          <table
            className={classNames("table table-striped", {
              "mb-0": downloadLists.length === 0,
            })}
            id="report_downloads_table"
          >
            <thead>
              <tr>
                {columnList.map((item, index) => (
                  <th key={`report_download_column_${index}`}>{item}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                downloadLists.length > 0 &&
                downloadLists.map((item, index) => (
                  <tr
                    className={classNames({
                      active: newReportDownloads.includes(item.SK),
                    })}
                    key={`${index + 1}`}
                  >
                    <td>{timeStringToTimeAMPM(item.RequestedDateTime)}</td>
                    <td>
                      {item.Status === "Completed" &&
                        item.FileName !== "NULL" && (
                          <button
                            type="button"
                            className={`verticalBtn ${
                              downloadedStatus[item.FileName] ? "active" : ""
                            }`}
                            id={`file_${index + 1}`}
                            onClick={() => {
                              downloadFile(item.FileUrl);
                              setDownloadedStatus((prevStatus) => ({
                                ...prevStatus,
                                [item.FileName]: true,
                              }));
                            }}
                          >
                            {item.FileName}
                          </button>
                        )}
                      {item.Status === "Failed" && (
                        <span>{t("downloadsFailed")}</span>
                      )}
                    </td>
                    <td>
                      <div className={item.Status.toLowerCase()}>
                        {item.Status === "InProgress" ? (
                          "In-Progress"
                        ) : (
                          <>{item.Status}</>
                        )}
                      </div>
                    </td>
                    <td>{generateReportType(item.ReportType)}</td>
                    <td>
                      <div className="filterLists">{renderFilters(item)}</div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="flex-grow empty-container">
          {!isLoading && !downloadLists.length && (
            <EmptyScreen
              img={Icons.emptyScreenReports}
              title="No Data to display!"
              desc={"No downloads available."}
            />
          )}
        </div>
        {isLoading && <LoadingSpinner />}
        <div className="bodyFooter">
          <Pagination
            currentPage={pageCount}
            totalPages={pageTotalCount}
            pageCallBack={setPageCount}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportDownloads;
